const PENDING_LOGOUT_STORAGE_RESET_KEY = 'app.logout.pending-storage-reset';

let logoutInProgress = false;
let logoutInProgressResetTimeout: number | undefined;

const LOGOUT_IN_PROGRESS_POLL_INTERVAL_MS = 100;
const LOGOUT_IN_PROGRESS_MAX_WAIT_MS = 5500;
const LOGOUT_IN_PROGRESS_RESET_TIMEOUT_MS = 5000;

export const clearLogoutInProgress = () => {
  logoutInProgress = false;
  if (logoutInProgressResetTimeout !== undefined) {
    window.clearTimeout(logoutInProgressResetTimeout);
    logoutInProgressResetTimeout = undefined;
  }
};

export const beginLogoutInProgress = () => {
  clearLogoutInProgress();
  logoutInProgress = true;
};

export const scheduleLogoutInProgressClear = () => {
  if (logoutInProgressResetTimeout !== undefined) {
    window.clearTimeout(logoutInProgressResetTimeout);
  }
  logoutInProgressResetTimeout = window.setTimeout(() => {
    clearLogoutInProgress();
  }, LOGOUT_IN_PROGRESS_RESET_TIMEOUT_MS);
};

export const isLogoutInProgress = () => logoutInProgress;

export const waitForLogoutInProgressToClear = async (): Promise<boolean> => {
  let waitedMs = 0;

  while (logoutInProgress && waitedMs < LOGOUT_IN_PROGRESS_MAX_WAIT_MS) {
    await new Promise<void>(resolve => {
      window.setTimeout(resolve, LOGOUT_IN_PROGRESS_POLL_INTERVAL_MS);
    });
    waitedMs += LOGOUT_IN_PROGRESS_POLL_INTERVAL_MS;
  }

  return !logoutInProgress;
};

export const isSameOriginLogoutRedirect = (
  logoutRedirectUri: string,
): boolean => {
  try {
    return new URL(logoutRedirectUri).origin === window.location.origin;
  } catch {
    return false;
  }
};

export const setPendingLogoutStorageReset = (): boolean => {
  try {
    sessionStorage.setItem(PENDING_LOGOUT_STORAGE_RESET_KEY, 'true');
    return true;
  } catch (e) {
    // `false` only means the marker write failed (e.g. storage unavailable);
    // it is not a signal about app boot phase/initial load.
    console.warn('setPendingLogoutStorageReset failed', e);
    return false;
  }
};

export const clearPendingLogoutStorageReset = () => {
  try {
    sessionStorage.removeItem(PENDING_LOGOUT_STORAGE_RESET_KEY);
  } catch (e) {
    console.warn('clearPendingLogoutStorageReset failed', e);
  }
};

interface ConsumePendingLogoutStorageResetArgs {
  resetAllStorageAtoms: () => void;
  clearOktaLocalStorage: () => void;
}

export const consumePendingLogoutStorageReset = ({
  resetAllStorageAtoms,
  clearOktaLocalStorage,
}: ConsumePendingLogoutStorageResetArgs): boolean => {
  try {
    if (sessionStorage.getItem(PENDING_LOGOUT_STORAGE_RESET_KEY) !== 'true') {
      return false;
    }
  } catch (error) {
    console.warn(
      'consumePendingLogoutStorageReset: sessionStorage read failed',
      error,
    );
    return false;
  }

  try {
    resetAllStorageAtoms();
    // Only clear Okta storage after atoms reset succeeds.
    // This prevents clearing a valid session if atoms reset fails and the marker
    // is retried on the next boot.
    clearOktaLocalStorage();
    clearPendingLogoutStorageReset();
    return true;
  } catch (error) {
    console.warn('Pending logout storage reset failed', error);
    // Keep marker so cleanup can retry on next boot without wiping Okta tokens.
    return false;
  }
};
